import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const looker: Competitor = {
  "id": "looker",
  "name": "Looker",
  "group": "bi",
  "website": "https://cloud.google.com/looker",
  "owner": "Google Cloud (acquired 2020)",
  "summary": "Google Cloud's enterprise BI platform built around LookML, a code-based semantic modeling layer that generates SQL against the warehouse at query time. It suits data teams that want centrally governed metrics consumed through dashboards, Explores, embedded analytics and AI agents.",
  "bestFor": [
    "Data teams with engineers comfortable writing LookML who want metrics defined once in version-controlled code",
    "Companies on Google Cloud / BigQuery that want tight integration with Gemini, Connected Sheets and Google's agent tooling",
    "Product teams embedding governed analytics or conversational agents for external customers (Embed edition)",
    "Organizations that need strict, centrally owned metric definitions across many consumers, including other BI tools via Looker's connectors"
  ],
  "notFor": [
    "Business users who expect to build their own analysis in a spreadsheet-style grid without a modeled Explore",
    "Small teams without a LookML developer: new data generally needs modeling before business users can explore it",
    "Buyers who want transparent, published per-seat pricing",
    "Teams that need native writeback/input tables into Snowflake without building custom action endpoints"
  ],
  "strengths": [
    "LookML is a mature, code-first semantic layer with git branching, development mode and CI validation, making governed metric definitions stronger and more reviewable than a workbook-centric model",
    "Conversational Analytics (GA November 2025) is grounded in the LookML model, can join up to five Explores, and can be embedded in custom apps via UI or API",
    "Strong developer tooling: GA VS Code extension for local LookML development, CI triggers from GitHub Actions/GitLab/Bitbucket, official API SDKs, and a Looker-managed MCP server (preview)",
    "Broad scheduled delivery destinations: email, Slack, webhooks, Amazon S3, SFTP and Action Hub integrations",
    "Semantic model can be consumed from Google Sheets (Connected Sheets) and other BI tools such as Tableau, Power BI and Excel through Looker connectors",
    "A customer-hosted (self-hosted) deployment is still offered for Looker (original)",
    "Deep Google Cloud integration (BigQuery, Gemini Enterprise, Knowledge Catalog)"
  ],
  "limitations": [
    "Pricing is not published: platform editions and user licenses are quote-only on annual 1-3 year commitments",
    "Business users mostly work within Explores that developers have modeled in LookML; self-service Explores on uploaded files were released as preview in Dec 2025 and GA status was announced at Next '26",
    "No native writeback feature; Google's own guidance builds writeback with the Action API plus a custom Cloud Run function",
    "No in-product Python runtime; Python is used only externally through the API SDK",
    "Signed embedding for external users requires the Embed edition on Looker (Google Cloud core)",
    "Self-hosting is only for Looker (original), and customer-hosted instances lose Gemini, the managed MCP server, Connected Sheets and Studio connectors",
    "Many 2026 AI features (dashboard agents, agentic workflows, Expression Assistant, Insight Assistant, managed MCP server) are still in preview",
    "Looker reports (the Studio-style report builder inside Looker) were deprecated in July 2026"
  ],
  "pricing": {
    "model": "platform fee by edition plus per-user licenses (Developer, Standard, Viewer), annual commitment",
    "publicStartingPrice": "not published (call sales)",
    "freeTier": "no - no Looker free tier is described on the pricing page; the general Google Cloud trial credit is not confirmed to apply. (Data Studio, formerly Looker Studio, is a separate free product.)",
    "notes": "Three editions - Standard (under 50 users), Enterprise and Embed - each including one production instance, 10 Standard Users and 2 Developer Users, and differing in API call limits (1K / 100K / 500K query API calls per month). Cost is driven by edition, user mix (Developer > Standard > Viewer) and term. Conversational Analytics includes monthly token allowances per edition; listed overage rates are $3 per 1M input and $20 per 1M output tokens, but Google says overage billing has not started and 90 days' notice will be given.",
    "sourceUrl": "https://cloud.google.com/looker/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "Looker generates SQL from LookML and runs it in the connected database at query time; persistent derived tables are optional caching in the warehouse.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/data-modeling"
    },
    "spreadsheet_interface": {
      "value": "partial",
      "note": "No spreadsheet grid. Table calculations use spreadsheet-like formulas on query results (permission-controlled), and Explores can be opened in Google Sheets via Connected Sheets.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/table-calculations"
    },
    "sql_editor": {
      "value": "yes",
      "note": "SQL Runner for ad hoc SQL and derived table debugging (Standard and Developer users).",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/data-modeling"
    },
    "python_support": {
      "value": "no",
      "note": "No in-product Python execution; an official Python SDK exists for calling the Looker API from outside.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    },
    "writeback": {
      "value": "partial",
      "note": "Possible only through the Action API with a custom endpoint (e.g., Cloud Run function); no native input tables.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/best-practices/bigquery-writeback-actions"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "LookML models define dimensions, measures, joins and Explores centrally.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/data-modeling"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Signed embedding for external users; requires Embed edition on Looker (Google Cloud core).",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/signed-embedding",
      "badge": "Embed edition"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Conversational Analytics GA since Nov 13, 2025; Visualization Assistant GA; dashboard agents and Expression/Insight Assistants in preview.",
      "sourceUrl": "https://cloud.google.com/blog/products/business-intelligence/looker-conversational-analytics-now-ga"
    },
    "version_control_git": {
      "value": "yes",
      "note": "LookML projects are git-backed with branches, commits, deploys and webhook deploy secrets.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/data-modeling"
    },
    "open_source_or_self_host": {
      "value": "partial",
      "note": "Not open source. Customer-hosted option exists only for Looker (original), with Gemini, managed MCP and some connectors unavailable.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/choosing-hosting-option"
    },
    "desktop_authoring_required": {
      "value": "no",
      "note": "Browser-based authoring; the VS Code extension (GA Sept 2026) is optional for local LookML development.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Email, Slack, webhook, S3, SFTP and Action Hub destinations.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/2618/scheduling"
    },
    "row_level_security": {
      "value": "yes",
      "note": "access_filter on Explores tied to user attributes adds a WHERE clause to every query (third-party guide; official docs page not opened).",
      "sourceUrl": "https://oneuptime.com/blog/post/2026-02-17-configure-looker-data-permissions-row-level-access/markdown"
    },
    "excel_export": {
      "value": "yes",
      "note": "Looks and Explores deliver or download as CSV and XLSX; dashboards as PDF or CSV ZIP.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/2618/scheduling"
    }
  },
  "recentChanges": [
    {
      "date": "2025-11",
      "change": "Conversational Analytics in Looker became generally available (Nov 13, 2025), after preview announced at Next in April 2025.",
      "sourceUrl": "https://cloud.google.com/blog/products/business-intelligence/looker-conversational-analytics-now-ga"
    },
    {
      "date": "2025-12",
      "change": "Self-service Explores on uploaded CSV/Excel files, tabbed dashboards and internal dashboard theming released in preview (Looker 25.20).",
      "sourceUrl": "https://cloud.google.com/blog/products/business-intelligence/looker-self-service-explores-tabbed-dashboards-custom-themes/"
    },
    {
      "date": "2026-04",
      "change": "At Google Cloud Next '26: self-service Explores, tabbed dashboards, paginated reporting, Visualization Assistant, CI/CD and embedded conversational agents announced GA; dashboard agents, agentic workflows, a new interface and a managed MCP server in preview.",
      "sourceUrl": "https://cloud.google.com/blog/products/business-intelligence/looker-updates-for-agentic-bi-at-next26"
    },
    {
      "date": "2026-04",
      "change": "Google renamed Looker Studio back to Data Studio (separate free/Pro product, not Looker itself).",
      "sourceUrl": "https://ppc.land/data-studio-is-back-google-kills-looker-studio-name-for-good/"
    },
    {
      "date": "2026-07",
      "change": "Looker reports (Studio-style reports inside Looker) deprecated; new reports can no longer be created.",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    },
    {
      "date": "2026-09",
      "change": "Looker VS Code extension reached GA; Semantic Search GA (26.16).",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    },
    {
      "date": "2026-10",
      "change": "Looker Data Apps entered preview: freeform dashboards built by local AI coding agents (26.18).",
      "sourceUrl": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    }
  ],
  "sources": [
    {
      "title": "Looker pricing",
      "url": "https://cloud.google.com/looker/pricing"
    },
    {
      "title": "Looker Conversational Analytics now GA",
      "url": "https://cloud.google.com/blog/products/business-intelligence/looker-conversational-analytics-now-ga"
    },
    {
      "title": "Looker updates for agentic BI at Next '26",
      "url": "https://cloud.google.com/blog/products/business-intelligence/looker-updates-for-agentic-bi-at-next26"
    },
    {
      "title": "Self-service Explores, tabbed dashboards, custom themes",
      "url": "https://cloud.google.com/blog/products/business-intelligence/looker-self-service-explores-tabbed-dashboards-custom-themes/"
    },
    {
      "title": "Looker release notes",
      "url": "https://docs.cloud.google.com/looker/docs/release-notes?authuser=4"
    },
    {
      "title": "Looker data modeling docs",
      "url": "https://docs.cloud.google.com/looker/docs/data-modeling"
    },
    {
      "title": "Choosing a Looker hosting option",
      "url": "https://docs.cloud.google.com/looker/docs/choosing-hosting-option"
    },
    {
      "title": "Looker scheduling docs",
      "url": "https://docs.cloud.google.com/looker/docs/2618/scheduling"
    },
    {
      "title": "BigQuery writebacks using Looker actions",
      "url": "https://docs.cloud.google.com/looker/docs/best-practices/bigquery-writeback-actions"
    },
    {
      "title": "Signed embedding",
      "url": "https://docs.cloud.google.com/looker/docs/signed-embedding"
    },
    {
      "title": "Using table calculations",
      "url": "https://docs.cloud.google.com/looker/docs/table-calculations"
    },
    {
      "title": "December 2025 Looker recap (Google developer forum)",
      "url": "https://discuss.google.dev/t/december-2025-looker-recap-unleashing-data-s-full-potential/298659"
    },
    {
      "title": "Looker row-level access filters (third party)",
      "url": "https://oneuptime.com/blog/post/2026-02-17-configure-looker-data-permissions-row-level-access/markdown"
    },
    {
      "title": "Data Studio is back (third party, Looker Studio rename)",
      "url": "https://ppc.land/data-studio-is-back-google-kills-looker-studio-name-for-good/"
    }
  ],
  "lastReviewed": "2026-11-03"
};
